import { axiosAPI, getSignedUrl } from 'utils';
import isMobileAppRuntime from 'utils/isMobileAppRuntime';
import { isOnline } from 'mobile-modules/offline/state';
import {
  hasOfflineAssetSync,
  listOfflineAssetKeysSync,
  resolveOfflineAssetKeySync,
} from 'mobile-modules/offline/assets';


const shouldUseOfflineStorage = () => isMobileAppRuntime() && !isOnline();

const readOfflineFile = async (key) => {
  const localUrl = resolveOfflineAssetKeySync(key);
  if (!localUrl) return null;

  const response = await fetch(localUrl, { cache: 'no-store' });
  if (!response.ok) return null;

  const cleanKey = String(key || '').split('?')[0].split('#')[0].toLowerCase();
  if (cleanKey.endsWith('.json')) {
    try { return await response.json(); } catch (_) { return null; }
  }
  return response.text();
};

const axiosConf = {
  transformRequest: [
    (data, headers) => {
      delete headers.common.Authorization;
      return data;
    },
  ],
};

export default {
  listFiles(key, uploadBucket) {
    if (shouldUseOfflineStorage()) {
      return Promise.resolve(
        listOfflineAssetKeysSync(key).map((offlineKey) => ({ key: offlineKey, Key: offlineKey })),
      );
    }

    return axiosAPI
      .post('/api/utils/list-files-in-folder', {
        key,
        uploadBucket,
      })
      .then((response) => {
        return response.data;
      });
  },
  checkIfFileExists(key, uploadBucket) {
    if (shouldUseOfflineStorage()) {
      return Promise.resolve(hasOfflineAssetSync(key));
    }

    return getSignedUrl({
      key,
      method: 'head',
      uploadBucket,
    })
      .then((url) => {
        return axiosAPI.head(url, axiosConf);
      })
      .then((response) => {
        return response.status === 200;
      })
      .catch((err) => {
        return false;
      });
  },
  
  /*getFile(key, uploadBucket, additionalAxiosParams = {}) {
    if (shouldUseOfflineStorage()) {
      return readOfflineFile(key).catch(() => null);
    }

    return getSignedUrl({
      key,
      method: 'get',
      uploadBucket,
    })
      .then((url) => {
        return axiosAPI.get(url, { ...axiosConf, ...additionalAxiosParams });
      })
      .then((response) => {
        return response.data;
      })
      .catch((err) => {
        return null;
      });
  },*/

  getFile(key, uploadBucket, additionalAxiosParams = {}, strict = false) {
    if (shouldUseOfflineStorage()) {
      return readOfflineFile(key).catch(() => null);
    }

    return getSignedUrl({
      key,
      method: 'get',
      uploadBucket,
    })
      .then((url) => {
        return axiosAPI.get(url, { ...axiosConf, ...additionalAxiosParams });
      })
      .then((response) => {
        return response.data;
      })
      .catch((err) => {
        if (strict && err.response?.status !== 404) throw err;
        return null;
      });
  },

  openPdf(key, uploadBucket) {
    if (shouldUseOfflineStorage()) {
      const localUrl = resolveOfflineAssetKeySync(key);
      if (!localUrl) {
        window.dispatchEvent(new CustomEvent('flc:offline-content-missing', { detail: { key } }));
        return Promise.resolve(false);
      }
      window.open(localUrl, '_blank');
      return Promise.resolve(true);
    }

    return getSignedUrl({
      key,
      method: 'get',
      uploadBucket,
      viewPdf: true,
    })
      .then((url) => window.open(url))
      .catch((err) => {
        console.log('Error opening pdf file', key, err);
      });
  },

  saveActivityData(id, data) {
  const endpoint = `/api/flink-make/activities/${id}/save-data`;

  const jsonStr = typeof data === 'string' ? data : JSON.stringify(data);
  const bytes = new Blob([jsonStr]).size;

  // API Gateway allows 10 MB, but the synchronous Lambda invocation behind
  // this endpoint is limited to 6 MB. Keep headroom for the proxy event.
  const TOO_BIG = bytes > 4_500_000;

  if (!TOO_BIG) {
    return axiosAPI
      .put(endpoint, data)
      .then((r) => r.status === 200)
      .catch((err) => {
        console.log('Saving activity data error', err);
        console.log('Saving activity data error: activityId', id);
        return false;
      });
  }

  // fallback через S3
  const key = `flink-make/activities/${id}/activity-data-${Date.now()}.json`;
  const blob = new Blob([jsonStr], { type: 'application/json' });

  return this.uploadFile(key, blob)
    .then((ok) => {
      if (!ok) return false;
      return axiosAPI
        .put(endpoint, { s3Key: key })
        .then((r) => r.status === 200)
        .catch((err) => {
          console.log('Saving activity data (notify) error', err);
          console.log('Saving activity data: s3Key', key);
          return false;
        });
    })
    .catch((err) => {
      console.log('Uploading JSON to S3 error', err);
      return false;
    });
},


  /*saveActivityData(id, data) {
    return axiosAPI
      .put(`/api/flink-make/activities/${id}/save-data`, data)
      .then((response) => {
        return response.status === 200;
      })
      .catch((err) => {
        console.log('Saving activity data error', err);
        console.log('Saving activity data error: activityId', id);
        console.log('Saving activity data error: data', data);
        return false;
      });
  },*/

  uploadFile(key, file, { onUploadProgress } = {}, uploadBucket = true) {
    return getSignedUrl({
      key,
      method: 'put',
      uploadBucket,
    })
      .then((url) => {
        return axiosAPI.put(url, file, {
          ...axiosConf,
          onUploadProgress,
        });
      })
      .then((response) => {
        return response.status === 200;
      })
      .catch((err) => {
        console.log('Uploading error', err);
        console.log('Uploading error: key', key);
        console.log('Uploading error: file', file);
        return false;
      });
  },
  uploadFiles(array) {
    // array of { key: String, file: File }
    return Promise.all(array.map((d) => this.uploadFile(d.key, d.file)));
  },
  copyFiles(array) {
    // array of {
    //   src: { uploadBucket: Boolean, key: String },
    //   dest: { uploadBucket: Boolean, key: String }
    // }
    return axiosAPI
      .post('/api/utils/copy-files', array)
      .then((response) => {
        console.log('Copy Response', response);
        return response.data;
      })
      .catch((err) => {
        console.log('Copy Error', err);
        return false;
      });
  },
  deleteFiles(keys, uploadBucket = true) {
    return axiosAPI
      .post('/api/utils/delete-files', { keys, uploadBucket })
      .then((results) => {
        return true;
      })
      .catch((err) => {
        console.log(err);
        return false;
      });
  },
};
